/* Footer: horizontally scrollable "first sentences" of the newest
 * books, each with a "Write like this" jump icon that navigates to
 * the book in the bookstore.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PenTool } from 'lucide-react';
import { P, FONTS, GRAIN_URL } from './palette';

export default function UnreadStackFooter({ books }) {
  const navigate = useNavigate();
  if (!books || books.length === 0) return null;

  return (
    <footer
      className="relative border-t"
      style={{ backgroundColor: P.paperDeep, borderColor: P.gold, color: P.ink }}
      data-testid="scriptorium-unread-stack"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{ backgroundImage: GRAIN_URL }}
      />

      <div className="relative mx-auto max-w-6xl px-6 py-8">
        <div className="mb-3 flex items-baseline justify-between">
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.32em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              The Unread Stack
            </div>
            <div
              className="mt-1 text-[13px]"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
            >
              First sentences from the Vault — open one, or write one like it.
            </div>
          </div>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          {books.slice(0, 10).map((b) => {
            const opening = (b.back_copy || '').split(/[.!?]/)[0].trim() || b.title;
            return (
              <div
                key={b.id}
                data-testid={`unread-stack-item-${b.id}`}
                className="flex min-w-[260px] max-w-[300px] flex-col justify-between rounded-md border p-3"
                style={{
                  backgroundColor: P.paper,
                  borderColor:     `${P.gold}88`,
                  boxShadow:       `0 4px 10px -6px ${P.leather}55`,
                }}
              >
                <p
                  className="text-[12.5px] leading-snug"
                  style={{ fontFamily: FONTS.type, color: P.ink }}
                >
                  “{opening.slice(0, 140)}{opening.length > 140 ? '…' : ''}”
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <div className="min-w-0">
                    <div
                      className="truncate text-[12px]"
                      style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.oxblood }}
                    >
                      {b.title}
                    </div>
                    <div className="truncate text-[10.5px]" style={{ color: P.inkSoft }}>
                      {b.author}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/bookstore#book=${encodeURIComponent(b.id)}`)}
                    data-testid={`unread-write-like-${b.id}`}
                    title="Write like this — jump to the book"
                    className="ml-2 flex h-8 w-8 items-center justify-center rounded-full transition hover:scale-105"
                    style={{ backgroundColor: P.oxblood, color: P.gold }}
                  >
                    <PenTool className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="mt-6 text-center text-[11px]"
          style={{ color: P.inkSoft }}
          data-testid="scriptorium-footer-copyright"
        >
          © 2026 Crown & Rose LLC · A scriptorium for finished novels.
        </div>
      </div>
    </footer>
  );
}
